<script setup lang="ts">
import { useI18n } from '@/i18n';
import { redirect } from '@/lib/redirect';
import { createLoginSchema, type LoginFormValues } from '@/schemas/login';
import { useAuthStore } from '@/stores/auth';
import { useUserStore } from '@/stores/user';
import { Alert, Button, Form, Input, LayoutGap } from '@dabble/quillui';
import AuthLayout from '@/components/AuthLayout.vue';
import InviteBanner from '@/components/InviteBanner.vue';
import { toTypedSchema } from '@vee-validate/yup';
import { useForm, useIsFormDirty } from 'vee-validate';
import { computed, ref } from 'vue';

const { t, te } = useI18n();

function translateFieldError(raw: string | undefined) {
  if (!raw) return '';
  return te(raw) ? t(raw) : raw;
}

const authStore = useAuthStore();
const userStore = useUserStore();

const error = ref<string | undefined>(undefined);

const rawLoginSchema = computed(() => createLoginSchema((key: string) => t(key)));
const validationSchema = computed(() => toTypedSchema(rawLoginSchema.value));

const { handleSubmit, defineField, values } = useForm<LoginFormValues>({
  validationSchema,
  initialValues: {
    email: '',
    password: '',
  },
});

const isValid = computed(() =>
  rawLoginSchema.value.isValidSync({
    email: values.email ?? '',
    password: values.password ?? '',
  }),
);

const isDirty = useIsFormDirty();

const fieldOpts = {
  validateOnBlur: true,
  validateOnChange: true,
  validateOnModelUpdate: true,
  props: (state: { errors: string[] }) => ({
    error: state.errors.length > 0,
    errorMessage: translateFieldError(state.errors[0]),
  }),
};

const [email, emailField] = defineField('email', fieldOpts);
const [password, passwordField] = defineField('password', fieldOpts);

const errorMessage = computed(() => {
  const raw = error.value ?? authStore.error;
  if (!raw) return '';
  return te(raw) ? t(raw) : raw;
});

const onSubmit = handleSubmit(async (values: LoginFormValues) => {
  error.value = undefined;
  try {
    const response = await authStore.login(values.email, values.password);
    if (response) {
      userStore.setUser(response.user);
      await redirect();
    }
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : String(e);
    error.value = te(message) ? t(message) : message;
  }
});
</script>

<template>
  <AuthLayout>
    <InviteBanner />
    <Form v-model:dirty="isDirty" :valid="isValid" :actionless="true" @submit="onSubmit">
      <LayoutGap direction="column" size="medium" align="center" class="login__form-contents">
        <div v-if="errorMessage" class="login__alert" role="alert" aria-live="assertive" aria-atomic="true">
          <Alert variant="danger">{{ errorMessage }}</Alert>
        </div>
        <LayoutGap direction="column" align="center" size="large" class="login__form-stack">
          <LayoutGap direction="column" size="small">
            <Input
              id="email"
              v-model="email"
              class="auth-field"
              v-bind="emailField"
              :aria-label="t('email')"
              type="text"
              required
              size="large"
              autocapitalize="off"
              :disabled="authStore.signingIn"
              :placeholder="t('signup_placeholder_email')"
            />
            <Input
              id="password"
              v-model="password"
              class="auth-field"
              v-bind="passwordField"
              :aria-label="t('password')"
              type="password"
              :placeholder="t('signup_placeholder_password')"
              required
              autocapitalize="off"
              size="large"
              :disabled="authStore.signingIn"
              autocomplete="current-password"
            >
              <template #note></template>
            </Input>
          </LayoutGap>
          <LayoutGap direction="column" align="center">
            <Button
              class="auth-submit"
              type="submit"
              :disabled="authStore.signingIn || !isValid"
              fill-width
              size="large"
            >
              {{ authStore.signingIn ? t('auth_logging_in') : t('auth_log_in') }}
            </Button>

            <router-link to="/account/lost-password">{{ t('auth_forgot_password') }}</router-link>
          </LayoutGap>
        </LayoutGap>
      </LayoutGap>
    </Form>

    <template #footer>
      <p class="login__signup-link">
        {{ t('auth_no_account') }}
        <router-link to="/auth/signup">{{ t('auth_sign_up') }}</router-link>
      </p>
    </template>
  </AuthLayout>
</template>

<style lang="scss" scoped>
@use '@/styles/auth-form.scss';

.login {
  &__signup-link {
    margin: 0;
  }
}
</style>
